import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Filter, Header } from './component';
import { DataTable, Excel } from 'CommonComponent';
import { getAccountManagerMonthlyReport, clearAccountManagerMonthlyReport, exportHandler } from 'actions';
import { isEmpty } from 'lodash';
import { useLocation } from 'react-router-dom';
import moment from "moment-timezone";
import './AccountManagerMonthlyReport.css';

const AccountManagerMonthlyReport = () => {
    const dispatch = useDispatch();
    const location = useLocation();
    
    //const [columns, setColumns] = useState([]);
    const [extraFiltersState, setExtraFiltersState] = useState({
        values: {
            year: moment().format('YYYY'),
            month: moment().format('M'),
            selectedDate: moment(),
            month_year: moment().format('YYYY-MM'),
            program_ids: [],
            user_ids: [],
            task_ids: [],
        }
    });

    const session = useSelector((s) => s.session);
    const { data, headers, loading } = useSelector(state => state.timesheetReportsState.accountManagerMonthly);
    const object_viewed_id = session?.current_page_permissions?.object_id || "";

    useEffect(() => {
        if (session.current_page_permissions.page_url === location.pathname) {
            filterTable();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [session.current_page_permissions]);

    const formatValue = (value) => {
        if (value === null || value === undefined || value === '') {
            return '-';
        }
        if (typeof value === 'number') {
            return <span>{value.toFixed(2)}</span>;
        }
        return value;
    };

    const formatPercentage = (value) => {
        if (value === null || value === undefined || value === '') {
            return '-';
        }
        if (typeof value === 'number') {
            return <span>{value.toFixed(2)}%</span>;
        }
        if (typeof value === 'string' && value) {
            return <span>{value.includes('%') ? value : value + '%'}</span>;
        }
        return '0.00%';
    };

    const { flatData, dynamicKeys, dynamicHeaders } = useMemo(() => {
        const flatData = [];
        const dynamicKeys = new Set();
        const dynamicHeaders = {};

        const rows = Array.isArray(data) ? data : [];
        rows.forEach((userData) => {
            let isFirstUserRow = true;

            if (userData && Array.isArray(userData.programs)) {
                userData.programs.forEach((prog) => {
                    const tasks = Array.isArray(prog.tasks) ? prog.tasks : [];
                    let isFirstProgramTask = true;

                    tasks.forEach((task) => {
                        const row = {
                            user_id: userData.user_id,
                            user_name: isFirstUserRow ? userData.user_name : '',
                            program_id: prog.program_id,
                            program_name: isFirstProgramTask ? prog.program_name : '',
                            task_id: task.task_id,
                            task_name: task.task_name,
                            category_name: task.category_name,
                            program_total_hours: task.task_total_hours || 0,
                            percentage: task.task_percentage_for_month || 0,
                            is_user_total: false,
                        };

                        Object.keys(task).forEach((key) => {
                            if (key.startsWith('week_ending_')) {
                                dynamicKeys.add(key);
                                row[key] = task[key];

                                if (headers && headers[key]) {
                                    dynamicHeaders[key] = headers[key];
                                } else if (!dynamicHeaders[key]) {
                                    const parts = key.replace('week_ending_', '').split('_');
                                    if (parts.length === 3) {
                                        dynamicHeaders[key] = `Week Ending ${parseInt(parts[0], 10)}/${parseInt(parts[1], 10)}/${parts[2]}`;
                                    } else {
                                        dynamicHeaders[key] = key;
                                    }
                                }
                            }
                        });

                        flatData.push(row);
                        isFirstUserRow = false;
                        isFirstProgramTask = false;
                    });
                });

                const userTotalRow = {
                    user_id: userData.user_id,
                    user_name: '',
                    program_id: '',
                    program_name: 'Total',
                    program_total_hours: userData.user_total_hours_for_month || 0,
                    percentage: userData.programs.reduce(
                        (sum, prog) => sum + (Number(prog.program_percentage_for_month) || 0),
                        0
                    ),
                    is_user_total: true,
                };

                dynamicKeys.forEach((weekKey) => {
                    userTotalRow[weekKey] = userData.programs.reduce(
                        (sum, prog) => sum + ((prog.tasks || []).reduce((taskSum, task) => taskSum + (Number(task[weekKey]) || 0), 0)),
                        0
                    );
                });

                flatData.push(userTotalRow);

                const spacerRow = {
                    user_id: '',
                    user_name: '',
                    program_id: '',
                    program_name: '',
                    task_id: '',
                    task_name: '',
                    program_total_hours: '',
                    percentage: '',
                    is_user_total: false,
                    is_empty_row: true,
                };

                dynamicKeys.forEach((weekKey) => {
                    spacerRow[weekKey] = '';
                });

                flatData.push(spacerRow);
            }
        });

        return { flatData, dynamicKeys: Array.from(dynamicKeys), dynamicHeaders };
    }, [data, headers]);

    const columns = useMemo(() => {
        const staticColsStart = [
            {
                id: "user_name",
                header: "User",
                accessor: (row) => row,
                sortable: false,
                searchable: true,
                cell: (row) => row.is_empty_row ? '' : (row.is_user_total ? '' : row.user_name),
            },
            {
                id: "program_name",
                header: "Program",
                accessor: (row) => row,
                sortable: false,
                searchable: true,
                cell: (row) => row.is_empty_row ? '' : (row.is_user_total ? <strong>{row.program_name}</strong> : row.program_name),
            },
            {
                id: "task_name",
                header: "Task",
                accessor: (row) => row,
                sortable: false,
                searchable: true,
                cell: (row) => row.is_empty_row ? '' : (row.is_user_total ? '' : row.task_name),
            }
        ];

        // Sort dynamic keys just in case. They contain dates so a basic sort might work if they are consistent.
        // e.g., "weekending of 1/04/2026 total hours"
        // Let's sort them string-wise or preserve API order. Preserving API order is safer.
        const dynamicCols = dynamicKeys.map(key => {
            return {
                id: key,
                header: dynamicHeaders[key] || key,
                accessor: (row) => row,
                cell: (row) => {
                    if (row.is_empty_row) {
                        return '';
                    }
                    return row.is_user_total ? <strong>{formatValue(row[key])}</strong> : formatValue(row[key]);
                },
                sortable: false,
                searchable: false,
            };
        });

        const staticColsEnd = [
            {
                id: "program_total_hours",
                header: "Total Hours",
                accessor: (row) => row,
                cell: (row) => {
                    if (row.is_empty_row) {
                        return '';
                    }
                    return row.is_user_total ? <strong>{formatValue(row.program_total_hours)}</strong> : formatValue(row.program_total_hours);
                },
                sortable: false,
                searchable: false,
            },
            {
                id: "percentage",
                header: "Percentage",
                accessor: (row) => row,
                cell: (row) => {
                    if (row.is_empty_row) {
                        return '';
                    }
                    return row.is_user_total ? <strong>{formatPercentage(row.percentage)}</strong> : formatPercentage(row.percentage);
                },
                sortable: false,
                searchable: false,
            }
        ];

        return [...staticColsStart, ...dynamicCols, ...staticColsEnd];
    }, [dynamicKeys, dynamicHeaders]);


    const filterTable = () => {
        if (!isEmpty(extraFiltersState.values.month_year)) {
            dispatch(getAccountManagerMonthlyReport({ ...extraFiltersState.values, object_viewed_id }));
        } else {
            dispatch(clearAccountManagerMonthlyReport());
        }
    };

    const handleExport = () => {
        // the API endpoint for export can be guessed dynamically based on other reports. 
        // e.g., timebyuser => 'timesheet/reports/monthly-user-timesheet/export'
        // Since we don't have exactly the export endpoint, let's guess it'll be mapped by standard 
        // or just let it hit 'timesheet/reports/account-manager-monthly-report/export'
        dispatch(exportHandler(
            'timesheet/reports/account-manager-monthly-report/export',
            extraFiltersState.values,
            object_viewed_id,
            'account_manager_monthly_report.xlsx'
        ));
    };

    return (
        <div className='wrapper-div'>
            <Header />
            <div className="d-flex justify-content-end align-items-center gap-3 p-4">
                <Excel handleExport={handleExport} />
            </div>
            <Filter onFilter={filterTable} extraFiltersState={extraFiltersState} setExtraFiltersState={setExtraFiltersState} />
            <DataTable
                columns={columns}
                data={flatData}
                serverSide={false}
                showSearch={false}
                loading={loading}
                rowClassName={(row) => (row.is_empty_row ? 'account-manager-monthly-spacer-row' : '')}
                className='table-container'
            />
        </div>
    );
};

export default AccountManagerMonthlyReport;
